import React, { useMemo, useState } from "react"
import { Download, Loader2, AlertTriangle, RotateCcw, Lock } from "lucide-react"
import { REPORT_REGIONS, formatWeekLabel } from "../data/reportsData"
import { useReports } from "../hooks/useReports"
import { useAuth } from "../hooks/useAuth"
import { downloadAllReports } from "../utils/downloadReport"
import ReportCard from "../components/reports/ReportCard"
import ReportsFilterBar from "../components/reports/ReportsFilterBar"
import WeekAccordionItem from "../components/reports/WeekAccordionItem"
import "./ReportsPage.css"

// "Region" filter options exclude the global report — it's toggled
// separately via the "Report type" filter.
const REGION_FILTER_OPTIONS = REPORT_REGIONS.filter((r) => r.key !== "global")

const ReportsPage = () => {
  const { reportWeeks, loading, error, refetch } = useReports()
  const { user } = useAuth()

  // "demo" accounts get the current week only — everything else (user,
  // admin) keeps full access. The actual enforcement has to live on the
  // backend download route too; this just hides/CTA's the archive here.
  const isArchiveLocked = user?.role === "demo"

  const [search, setSearch] = useState("")
  const [year, setYear] = useState("all")
  const [month, setMonth] = useState("all")
  const [region, setRegion] = useState("all")
  const [reportType, setReportType] = useState("all")
  const [expandedWeeks, setExpandedWeeks] = useState(() => new Set())

  const currentWeek = useMemo(
    () => reportWeeks.find((w) => w.isCurrent),
    [reportWeeks]
  )
  const archiveWeeks = useMemo(
    () => reportWeeks.filter((w) => !w.isCurrent),
    [reportWeeks]
  )

  const years = useMemo(() => {
    const unique = new Set(
      reportWeeks.map((w) => new Date(`${w.weekOf}T00:00:00`).getFullYear())
    )
    return Array.from(unique).sort((a, b) => b - a)
  }, [reportWeeks])

  // Does a single report match the Region / Report type filters?
  const matchesReportFilters = (report) => {
    if (reportType === "global" && report.key !== "global") return false
    if (reportType === "regional" && report.key === "global") return false
    if (region !== "all" && report.key !== region) return false
    return true
  }

  // Does a week match the Year / Month filters?
  const matchesWeekDate = (week) => {
    const date = new Date(`${week.weekOf}T00:00:00`)
    if (year !== "all" && date.getFullYear().toString() !== year) return false
    if (month !== "all" && date.getMonth().toString() !== month) return false
    return true
  }

  // Reports from a given week visible under all active filters —
  // shared by the current week panel and each archive row, so search
  // actually narrows down what's on screen everywhere.
  const getVisibleReports = (week) => {
    if (!matchesWeekDate(week)) return []

    const query = search.trim().toLowerCase()
    const weekLabelMatches = query
      ? formatWeekLabel(week.weekOf).toLowerCase().includes(query)
      : false

    return week.reports.filter((report) => {
      if (!matchesReportFilters(report)) return false
      if (!query) return true
      if (weekLabelMatches) return true
      return (
        report.title.toLowerCase().includes(query) ||
        report.summary.toLowerCase().includes(query)
      )
    })
  }

  const currentWeekReports = useMemo(
    () => (currentWeek ? getVisibleReports(currentWeek) : []),
    [currentWeek, region, reportType, search, year, month]
  )

  const visibleArchiveWeeks = useMemo(() => {
    return archiveWeeks
      .map((week) => ({ week, reports: getVisibleReports(week) }))
      .filter(({ reports }) => reports.length > 0)
  }, [archiveWeeks, region, reportType, search, year, month])

  const hasActiveFilters =
    search.trim() !== "" ||
    year !== "all" ||
    month !== "all" ||
    region !== "all" ||
    reportType !== "all"

  const handleClearFilters = () => {
    setSearch("")
    setYear("all")
    setMonth("all")
    setRegion("all")
    setReportType("all")
  }

  const toggleWeek = (weekId) => {
    setExpandedWeeks((prev) => {
      const next = new Set(prev)
      if (next.has(weekId)) next.delete(weekId)
      else next.add(weekId)
      return next
    })
  }

  if (loading) {
    return (
      <div style={{ padding: "20px" }}>
        <h2 className="page-title">Insight Reports</h2>
        <div className="reports-status">
          <Loader2 size={18} className="reports-status-spin" />
          Loading reports…
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: "20px" }}>
        <h2 className="page-title">Insight Reports</h2>
        <div className="reports-status">
          <AlertTriangle size={18} />
          Couldn't load reports — {error.message}
        </div>
        <button type="button" className="download-all-btn reports-retry-btn" onClick={refetch}>
          <RotateCcw size={14} />
          Retry
        </button>
      </div>
    )
  }

  return (
    <div style={{ padding: "20px" }}>
      <h2 className="page-title">Insight Reports</h2>

      <p className="reports-subtitle">
        Access the latest reports or browse previous weekly editions. <br /> Each
        weekly folder includes the Global Impact Report and three regional
        reports covering Asia, the Middle East, and Sub-Saharan Africa.
      </p>

      <ReportsFilterBar
        search={search}
        onSearchChange={setSearch}
        year={year}
        onYearChange={setYear}
        years={years}
        month={month}
        onMonthChange={setMonth}
        region={region}
        onRegionChange={setRegion}
        regionOptions={REGION_FILTER_OPTIONS}
        reportType={reportType}
        onReportTypeChange={setReportType}
        onClear={handleClearFilters}
      />

      {currentWeek && (
        <div className="current-week-panel">
          <div className="current-week-header">
            <div>
              <span className="current-week-eyebrow">Latest Week</span>
              <div className="current-week-title-row">
                <h3>{formatWeekLabel(currentWeek.weekOf)}</h3>
                {currentWeek.isNew && <span className="new-badge">NEW</span>}
              </div>
            </div>

            {currentWeekReports.length > 0 && (
              <button
                type="button"
                className="download-all-btn"
                onClick={() => downloadAllReports(currentWeekReports)}
              >
                <Download size={15} />
                Download All ({currentWeekReports.length} PDFs)
              </button>
            )}
          </div>

          {currentWeekReports.length > 0 ? (
            <div className="reports-grid">
              {currentWeekReports.map((report) => (
                <ReportCard key={report.id} report={report} />
              ))}
            </div>
          ) : (
            <p className="reports-empty-inline">No reports match your filters.</p>
          )}
        </div>
      )}

      <div className="archive-section">
        <span className="archive-eyebrow">Archive</span>

        {isArchiveLocked ? (
          <div className="archive-locked-preview">
            {/* Decorative only — onToggle is a no-op and pointer-events
                are disabled in CSS, so nothing here is actually clickable.
                Unfiltered archiveWeeks (not visibleArchiveWeeks) since
                this is just a preview, not real content to browse. */}
            <div className="week-accordion-list archive-locked-list" aria-hidden="true">
              {archiveWeeks.slice(0, 3).map((week) => (
                <WeekAccordionItem
                  key={week.id}
                  week={week}
                  reports={week.reports}
                  expanded={false}
                  onToggle={() => {}}
                />
              ))}
            </div>

            <div className="archive-locked-fade" />

            <div className="archive-locked-overlay">
              <div className="archive-locked-icon">
                <Lock size={20} />
              </div>
              <p className="archive-locked-title">Unlock the full report archive</p>
              <p className="archive-locked-subtitle">
                Demo accounts have access to the current week only. Upgrade your plan to access previous reports.
              </p>
              <button type="button" className="archive-upgrade-btn">
                Upgrade to Premium
              </button>
            </div>
          </div>
        ) : visibleArchiveWeeks.length > 0 ? (
          <div className="week-accordion-list">
            {visibleArchiveWeeks.map(({ week, reports }) => (
              <WeekAccordionItem
                key={week.id}
                week={week}
                reports={reports}
                expanded={expandedWeeks.has(week.id)}
                onToggle={() => toggleWeek(week.id)}
              />
            ))}
          </div>
        ) : (
          <p className="reports-empty-inline">
            {hasActiveFilters
              ? "No archived weeks match your filters."
              : "No archived reports yet."}
          </p>
        )}
      </div>
    </div>
  )
}

export default ReportsPage
